import React, {useState, useEffect, useContext} from 'react';
import calcProfitAm from '../../utils/systems/calcProfit';
import {useNavigate} from 'react-router-dom';
import SystemDisp from '../pages/System/SystemDisp';
import BacktestError from './BacktestError';
import { SystemContext } from './SystemBuilder';
import { useDeepCompareEffect } from '../../hooks/deepCompareHook';
import { PrimaryBtn, SecondaryBtn } from '../Assets/Buttons';
// RTK Mutations
import { useBacktestSystemMutation, useCreateSystemMutation, useUpdateSystemMutation } from '../../services/api/systemsApi';
const BacktestDisp = (props) => {
    const sysContext = useContext(SystemContext)
    const navigate = useNavigate();
    const [complete, setComplete] = useState(false);
    const [system, setSystem] = useState({});

    // RTK Mutations
    const [backtestSystem, { isLoading: isBacktestSystemLoading, isError: isBacktestSystemError, error: backtestError }] = useBacktestSystemMutation();
    const [createSystem, { isLoading: isCreateSystemLoading, isError: isCreateSystemError }] = useCreateSystemMutation();
    const [updateSystem, { isLoading: isUpdateSystemLoading, isError: isUpdateSystemError }] = useUpdateSystemMutation();
    // set system when system context changes
    useEffect(()=>{
        const tmpSys = {
            ...system,
            sport: sysContext.sport,
            name: sysContext.name,
            criteria: sysContext.criteria,
            bet: sysContext.bet,
            matches: sysContext.matches,
            units: 1,
            roi: props.roi,
            w: props.w,
            l: props.l, 
            p: props.p
        };
        setSystem(tmpSys);
    },[sysContext, props.roi, props.w, props.l, props.p]);

    // function get to get wl data
    const analyzeSystemPerformance = async() => {
        // total win loss and push count
        let total_w = 0, total_l = 0, total_p = 0, total_prof = 0;
        let seasons = Object.keys(sysContext.matches);
        // tmp sznWL arr
        let tmpSznWL = [];
        for(const szn of seasons){
            let games = sysContext.matches[szn];
            // season win loss and push count
            let sznW = 0, sznL = 0, sznP = 0, sznProf = 0;
            if(games.length > 0){
                for(const game of games){
                    let betProf;
                    if(game.bet.result === "W"){
                        total_w = total_w+1;
                        sznW = sznW+1;
                        betProf = calcProfitAm(game.bet.odds, sysContext.units);
                        sznProf = sznProf + betProf;
                        total_prof = total_prof + betProf;
                    }else if(game.bet.result === "L"){
                        sznL = sznL+1;
                        total_l = total_l+1;
                        sznProf = sznProf - sysContext.units;
                        total_prof = total_prof - sysContext.units;
                    }else if(game.bet.result === "P"){
                        sznP = sznP+1;
                        total_p = total_p+1;
                    };
                };
            };
            tmpSznWL.push({season: szn, w: sznW, l:sznL, p:sznP, prof:sznProf.toFixed(2)});
        };
        let tmpRoi = (total_w + total_l) > 0 ? (total_prof / ((total_w + total_l) * sysContext.units)) * 100 : 0;

        props.setRoi(parseFloat(tmpRoi.toFixed(2)))
        props.setW(total_w);
        props.setL(total_l);
        props.setP(total_p);
        props.setSznWL(tmpSznWL);
    };

    // Analyze system performance when sysMatches changes
    useDeepCompareEffect(() => {
        if (Object.keys(sysContext.matches).length > 0) {
            analyzeSystemPerformance();
        };
    }, [sysContext.matches]);

    // function to handle the finishing of backtesting
    const finished_backtesting = async(sysMatches, upcomingMatches) =>{
        // set state variables
        props.setSysMatches(sysMatches);
        props.setUpcomingMatches(upcomingMatches);
        setComplete(true);
        props.setBacktested({
            tested: true, 
            modified: false
        });
    };

    // function to backtest the system
    const fetch_backtest = async() => {
        // body of post request
        const body = {
            sport: sysContext.sport,
            name: sysContext.name,
            criteria: sysContext.criteria,
            bet: sysContext.bet,
        };
        try {
            // response of api request
            const payload = await backtestSystem(body).unwrap();
            // set data once returned
            finished_backtesting(payload.system_matches, payload.upcoming_matches);
        } catch(error) {
            setComplete(true);
            console.error('Error backtesting system:', error.data.error);
        }
    };

    // run when system changes, backtest system
    useEffect(()=>{
        // backtest only if initial backtest request or modifications were made
        if(props.backtested.tested === false || props.backtested.modified === true){
            fetch_backtest();
        }else{
            setComplete(true);
        };
    },[props.backtested]);
    
    //Function to create system record in database
    async function handleCreateSystem() {
        // body of post request
        const body = {
            system:{
                sport: sysContext.sport,
                name: sysContext.name,
                criteria: sysContext.criteria,
                bet: sysContext.bet,
                matches: sysContext.matches,
            },
            upcomingMatches: sysContext.upcomingMatches,
            type: sysContext.sysType
        };
        try{
            // call api
            await createSystem(body).unwrap();
            // navigate back to systems page
            navigate('/systems');
        }catch(error){
            console.error('Error creating system:', error);
        };
    };

    //Function to update system record in database
    async function handleUpdateSystem() {
        // body of post request
        const body = {
            sys: {
                sport: sysContext.sport,
                name: sysContext.name,
                criteria: sysContext.criteria,
                bet: sysContext.bet,
                matches: sysContext.matches,
            },
            id: sysContext.id,
            upcomingMatches: sysContext.upcomingMatches
        };
        try{
            // call api
            await updateSystem(body).unwrap();
            // navigate back to systems page 
            navigate('/systems');
        }catch(error){
            console.error('Error updating system:', error);
        };
    };

    // function to handle submit
    const submit = () =>{
        // either create system or update depending on type
        if(props.pageType === "edit"){
            handleUpdateSystem();
        }else handleCreateSystem();
    };

    if(complete){
        // If there's an error, display error component
        if(isBacktestSystemError){
            return <BacktestError error={backtestError} />;
        }

        // Otherwise display the system results
        return(
            <div className="relative pb-20">
                <div className="space-y-8">
                    <SystemDisp system={system} sznWL={props.sznWL} pageType="edit"/>
                </div>
                
                <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-brand-dark-900 border-t border-gray-200 dark:border-gray-700 shadow-lg">
                    <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                Ready to {props.pageType === "edit" ? "update" : "save"} your system?
                            </span>
                            <div className="flex items-center justify-end space-x-4">
                                <SecondaryBtn
                                    text="Edit Criteria"
                                    onClick={() => sysContext.setStep(2)}
                                />
                                <PrimaryBtn
                                    text={props.pageType === "edit" ? "Update System" : "Save System"}
                                    onClick={() => submit()}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }else{
        return(
            <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-red-500"></div>
            </div>
        )
    }
};

export default BacktestDisp